/**
 * Everything a member told us, and everything we captured, during onboarding.
 *
 * The answers read returns the stored preferences document as-is, so it can
 * carry keys this screen has never heard of. Known keys get a written label;
 * any other key is humanised and shown under "Other answers" rather than
 * dropped, so nothing the member stored is invisible to staff.
 *
 * Each topic opens as a one-line summary and expands to the full detail. The
 * screen shows only what was stored: no scores, percentages or derived
 * judgements are computed here.
 */

import type { ReactNode } from 'react';
import {
  memberOnboardingAnswersApiV1InternalUsersIdOnboardingAnswersGet,
  type AdminMemberColorAnalysis,
  type AdminMemberConsentEvent,
  type AdminMemberLegalConsent,
  type AdminMemberOnboardingAnswers,
  type AdminMemberOnboardingReminder,
  type AdminMemberOnboardingScan,
  type ConsumerDetail,
} from '@closet-theory/api';
import { ChevronDown } from '@untitledui/icons';
import { Section } from '@ui/console/primitives';
import { humanize } from '@ui/console/format';
import { DetailList, Status } from './components';
import { GarmentImage, SignatureExpiryBoundary } from './garment-image';
import { InsightState, useInsight, when } from './consumer-insights';

/** The answers read signs its private captures for five minutes. */
export const ANSWERS_SIGNED_URL_TTL_MS = 300_000;

type Doc = Record<string, unknown>;

/* ---------------------------------------------------------------- labels */

/** Keys compare without case, spaces, dashes or underscores: `work_mode` is `workMode`. */
function norm(key: string) {
  return key.replace(/[\s_-]+/g, '').toLowerCase();
}

const KEY_LABELS: Record<string, string> = {
  // About and shopping
  clothingsection: 'Shops in',
  gender: 'Gender',
  genderpresentation: 'Gender presentation',
  age: 'Age',
  expectations: 'What they hope to get from us',
  expectation: 'What they hope to get from us',
  goal: 'Main goal',
  frustrations: 'What frustrates them about getting dressed',
  frustration: 'What frustrates them about getting dressed',
  brandsliked: 'Brands they like',
  brandsavoided: 'Brands they avoid',
  shoppingfrequency: 'How often they shop',
  budget: 'Usual budget',
  preferredcategories: 'Kinds of clothing they prefer',
  socialfrequency: 'How often they go out socially',
  // Sizes and fit
  sizeprofile: 'Sizes',
  general: 'Usual size',
  top: 'Top sizes',
  bottomwaist: 'Trouser waist',
  bottomlength: 'Trouser length',
  dresslength: 'Dress length',
  skirtlength: 'Skirt length',
  trousertypes: 'Trouser styles',
  trousercuts: 'Trouser cuts',
  fitsloved: 'Fits they love',
  fitsavoided: 'Fits they avoid',
  // Style
  aestheticleanings: 'Styles they lean towards',
  colourpreferences: 'Colour preferences',
  love: 'Colours they love',
  avoid: 'Colours they avoid',
  occasionmix: 'Occasion mix',
  styleswipes: 'Style swipes',
  stylesignals: 'Style signals',
  styledeckcompletedat: 'Style deck finished',
  verdict: 'Reaction',
  // Work and week
  workstatus: 'Work status',
  workstatusother: 'Work status, in their words',
  occupation: 'Occupation',
  workmode: 'Where they work',
  workdays: 'Days they work',
  dayoffs: 'Days off',
  weekdayactivities: 'Weekday activities',
  weekendactivities: 'Weekend activities',
  workoutfits: 'What they wear to work',
  workdress: 'Work dress code',
  outfitsets: 'Outfit sets they wear',
  sleeves: 'Sleeve preferences',
  necklines: 'Neckline preferences',
  weeklyroutine: 'Weekly routine',
  type: 'Type',
  description: 'Description',
  // Photos
  capturechoice: 'Who took the photos',
  // Colour analysis
  season: 'Season',
  undertone: 'Undertone',
  depth: 'Depth',
  temperature: 'Temperature',
  palette: 'Palette',
  palettebins: 'Palette families',
  summary: 'Summary',
  source: 'Analysed from',
  // Scan
  heightmm: 'Height',
  weightg: 'Weight',
  scannedvia: 'Scanned with',
  status: 'Status',
  createdat: 'Scanned at',
};

/** A label for any stored key: the written one, or the key in plain words. */
export function answerLabel(key: string): string {
  const known = KEY_LABELS[norm(key)];
  if (known) return known;
  // `dayOffs` -> `day Offs` -> `Day offs`; snake and kebab keys go the same way.
  const spaced = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return humanize(spaced);
}

const DAYS: Record<string, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  tues: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  thur: 'Thursday',
  thurs: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};

const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/** Keys whose values the member typed themselves, shown exactly as written. */
const VERBATIM = new Set(
  ['brandsliked', 'brandsavoided', 'occupation', 'workstatusother', 'summary', 'description'].map(
    norm,
  ),
);

/** A stored option id such as `work_from_home`, as words. */
function optionLabel(value: string): string {
  const day = DAYS[value.toLowerCase()];
  if (day) return day;
  return humanize(value.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase());
}

/* ---------------------------------------------------------------- values */

function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return !value.trim();
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

function isDoc(value: unknown): value is Doc {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function NotAnswered({ children = 'Not answered' }: { children?: ReactNode }) {
  return <span className="font-normal text-tertiary">{children}</span>;
}

function Swatch({ hex }: { hex: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        aria-hidden="true"
        className="inline-block size-4 shrink-0 rounded-full border border-secondary"
        style={{ backgroundColor: hex }}
      />
      <span className="font-mono text-xs">{hex}</span>
    </span>
  );
}

function Chips({ items }: { items: ReactNode[] }) {
  return (
    <ul className="flex flex-wrap gap-1.5">
      {items.map((item, index) => (
        <li
          key={index}
          className="rounded-full border border-secondary bg-secondary px-2 py-0.5 text-xs text-secondary"
        >
          {item}
        </li>
      ))}
    </ul>
  );
}

function scalarText(value: unknown, verbatim: boolean): ReactNode {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return value.toLocaleString();
  if (typeof value === 'string') {
    const text = value.trim();
    if (HEX.test(text)) return <Swatch hex={text} />;
    if (TIMESTAMP.test(text)) return when(text);
    return verbatim ? text : optionLabel(text);
  }
  return String(value);
}

/**
 * Any stored value, in words. Lists become chips, documents become a nested
 * labelled list, and an empty value reads "Not answered".
 */
export function AnswerValue({ value, field }: { value: unknown; field?: string }) {
  if (isEmpty(value)) return <NotAnswered />;
  const verbatim = field ? VERBATIM.has(norm(field)) : false;
  if (Array.isArray(value)) {
    const items: unknown[] = value;
    if (items.every((item) => !isDoc(item) && !Array.isArray(item)))
      return <Chips items={items.map((item) => scalarText(item, verbatim))} />;
    return (
      <ul className="flex flex-col gap-2">
        {items.map((item, index) => (
          <li key={index}>
            <AnswerValue value={item} {...(field ? { field } : {})} />
          </li>
        ))}
      </ul>
    );
  }
  if (isDoc(value)) return <AnswerDoc doc={value} />;
  return <>{scalarText(value, verbatim)}</>;
}

function AnswerDoc({ doc }: { doc: Doc }) {
  return (
    <dl className="flex flex-col gap-1.5 rounded-lg border border-secondary bg-secondary/40 p-2.5">
      {Object.entries(doc).map(([key, value]) => (
        <div key={key} className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <dt className="text-xs text-tertiary">{answerLabel(key)}:</dt>
          <dd className="m-0 min-w-0 text-sm font-medium text-secondary">
            <AnswerValue value={value} field={key} />
          </dd>
        </div>
      ))}
    </dl>
  );
}

/* ---------------------------------------------------------------- lookups */

/**
 * Finds one answer in the preferences document or its styling context, by
 * normalised key. `unclaimed` is every stored key no topic shows.
 */
class Answers {
  constructor(
    readonly preferences: Doc,
    readonly context: Doc,
  ) {}

  get(key: string): unknown {
    const wanted = norm(key);
    for (const doc of [this.preferences, this.context]) {
      for (const [stored, value] of Object.entries(doc)) {
        if (norm(stored) === wanted && !isEmpty(value)) return value;
      }
    }
    return undefined;
  }

  rows(keys: string[]): Array<[ReactNode, ReactNode]> {
    return keys.map((key) => [answerLabel(key), <AnswerValue value={this.get(key)} field={key} />]);
  }

  unclaimed(): Array<{ key: string; value: unknown }> {
    const shown = new Set(SHOWN_KEYS.map(norm));
    return [...Object.entries(this.preferences), ...Object.entries(this.context)]
      .filter(([key]) => !shown.has(norm(key)))
      .map(([key, value]) => ({ key, value }));
  }
}

function text(value: unknown): string | undefined {
  if (isEmpty(value)) return undefined;
  if (typeof value === 'string') return optionLabel(value);
  if (typeof value === 'number') return String(value);
  if (Array.isArray(value))
    return value
      .filter((item) => typeof item === 'string' || typeof item === 'number')
      .map((item) => (typeof item === 'string' ? optionLabel(item) : String(item)))
      .join(', ');
  return undefined;
}

/** A summary line from whichever values exist, or "Not answered". */
function digest(parts: Array<string | undefined | false | null>): string {
  const present = parts.filter((part): part is string => Boolean(part));
  return present.length ? present.join(' · ') : 'Not answered';
}

/* ---------------------------------------------------------------- layout */

function Topic({
  title,
  summary,
  children,
}: {
  title: string;
  summary: string;
  children: ReactNode;
}) {
  return (
    <details className="group rounded-xl border border-secondary bg-primary" data-topic={title}>
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 p-4 [&::-webkit-details-marker]:hidden">
        <div className="flex min-w-0 flex-col gap-0.5">
          <h3 className="text-sm font-semibold text-primary">{title}</h3>
          <p className="truncate text-sm text-tertiary">{summary}</p>
        </div>
        <ChevronDown
          aria-hidden="true"
          className="size-5 shrink-0 text-fg-quaternary transition-transform group-open:rotate-180"
        />
      </summary>
      <div className="flex flex-col gap-5 border-t border-secondary p-4">{children}</div>
    </details>
  );
}

function More({ label, children }: { label: string; children: ReactNode }) {
  return (
    <details className="text-sm">
      <summary className="cursor-pointer text-sm font-medium text-brand-secondary">{label}</summary>
      <div className="mt-3">{children}</div>
    </details>
  );
}

function Sub({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-tertiary">{title}</h4>
      {children}
    </div>
  );
}

function Table({
  label,
  columns,
  rows,
}: {
  label: string;
  columns: string[];
  rows: ReactNode[][];
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-secondary">
      <table className="w-full text-left text-sm" aria-label={label}>
        <thead className="bg-secondary text-xs text-tertiary">
          <tr>
            {columns.map((column) => (
              <th key={column} scope="col" className="px-3 py-2 font-medium">
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-secondary">
          {rows.map((row, index) => (
            <tr key={index}>
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className="px-3 py-2 align-top text-secondary">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function KeyValueTable({ label, doc }: { label: string; doc: Doc | null | undefined }) {
  if (!doc || isEmpty(doc)) return <NotAnswered>Nothing recorded</NotAnswered>;
  return (
    <Table
      label={label}
      columns={['Measurement', 'Value']}
      rows={Object.entries(doc).map(([key, value]) => [
        answerLabel(key),
        <AnswerValue value={value} field={key} />,
      ])}
    />
  );
}

function Photo({
  url,
  alt,
  absentDetail,
  onRefresh,
}: {
  url: string | null | undefined;
  alt: string;
  absentDetail: string;
  onRefresh: () => void | Promise<void>;
}) {
  return (
    <figure className="flex min-w-0 flex-col gap-2">
      <GarmentImage
        url={url ?? null}
        alt={alt}
        absentTitle="Not captured"
        absentDetail={absentDetail}
        onRefresh={onRefresh}
      />
      <figcaption className="text-xs font-medium text-secondary">{alt}</figcaption>
    </figure>
  );
}

/* ---------------------------------------------------------------- topics */

const ABOUT_KEYS = [
  'clothing_section',
  'gender',
  'gender_presentation',
  'age',
  'goal',
  'expectations',
  'frustrations',
  'brandsLiked',
  'brandsAvoided',
  'shoppingFrequency',
  'budget',
  'preferredCategories',
  'socialFrequency',
];

function AboutTopic({ answers }: { answers: Answers }) {
  const rows = answers.rows(ABOUT_KEYS);
  return (
    <Topic
      title="About & shopping"
      summary={digest([
        text(answers.get('clothing_section')),
        text(answers.get('gender')),
        typeof answers.get('age') === 'number' ? `Age ${String(answers.get('age'))}` : undefined,
        text(answers.get('budget')) && `Budget: ${text(answers.get('budget'))}`,
      ])}
    >
      <DetailList items={rows} />
    </Topic>
  );
}

const SIZE_KEYS = [
  'general',
  'top',
  'bottom_waist',
  'bottom_length',
  'dress_length',
  'skirt_length',
  'trouser_types',
  'trouser_cuts',
];

function SizesTopic({ answers, data }: { answers: Answers; data: AdminMemberOnboardingAnswers }) {
  const sizes: Doc = isDoc(answers.preferences['size_profile'])
    ? answers.preferences['size_profile']
    : {};
  const known = new Set(SIZE_KEYS.map(norm));
  const extraSizes = Object.entries(sizes).filter(([key]) => !known.has(norm(key)));
  const sizeRows: Array<[ReactNode, ReactNode]> = [
    ...SIZE_KEYS.map((key): [ReactNode, ReactNode] => [
      answerLabel(key),
      <AnswerValue value={sizes[key]} field={key} />,
    ]),
    ...extraSizes.map(([key, value]): [ReactNode, ReactNode] => [
      answerLabel(key),
      <AnswerValue value={value} field={key} />,
    ]),
  ];
  const monk = data.skin_tone_monk;
  return (
    <Topic
      title="Sizes & fit"
      summary={digest([
        text(sizes['general']) && `Usual size ${text(sizes['general'])}`,
        text(sizes['top']) && `Tops ${text(sizes['top'])}`,
        text(sizes['bottom_waist']) && `Waist ${text(sizes['bottom_waist'])}`,
        monk != null && `Skin tone ${monk}`,
      ])}
    >
      <Sub title="Sizes">
        <DetailList items={sizeRows} />
      </Sub>
      <Sub title="Fit">
        <DetailList items={answers.rows(['fits_loved', 'fits_avoided'])} />
      </Sub>
      <Sub title="Body and skin">
        <DetailList
          items={[
            [
              'Skin tone (Monk scale, 1 lightest to 10 deepest)',
              monk == null ? <NotAnswered /> : String(monk),
            ],
          ]}
        />
        <More label="Body measurements they entered">
          <KeyValueTable label="Body measurements" doc={data.body_measurements} />
        </More>
      </Sub>
    </Topic>
  );
}

const REACTION_ORDER = ['love', 'like', 'skip', 'dislike'];

function reactionCounts(reactions: string[]) {
  const counts = new Map<string, number>();
  for (const reaction of reactions) counts.set(reaction, (counts.get(reaction) ?? 0) + 1);
  return [...counts].sort(
    ([left], [right]) =>
      (REACTION_ORDER.indexOf(left) + 1 || 99) - (REACTION_ORDER.indexOf(right) + 1 || 99),
  );
}

function StyleTopic({ answers }: { answers: Answers }) {
  const swipesRaw = answers.get('style_swipes');
  const signalsRaw = answers.get('style_signals');
  const swipes = isDoc(swipesRaw) ? Object.entries(swipesRaw) : [];
  const signals = isDoc(signalsRaw) ? Object.entries(signalsRaw) : [];
  const swipeCounts = reactionCounts(swipes.map(([, reaction]) => String(reaction)));
  return (
    <Topic
      title="Style"
      summary={digest([
        text(answers.get('aesthetic_leanings')),
        swipes.length
          ? swipeCounts.map(([reaction, count]) => `${count} ${optionLabel(reaction)}`).join(', ')
          : undefined,
      ])}
    >
      <DetailList
        items={[
          ...answers.rows(['aesthetic_leanings', 'colour_preferences', 'occasion_mix']),
          [
            'Style deck finished',
            <AnswerValue value={answers.preferences['style_deck_completed_at']} />,
          ],
        ]}
      />
      <Sub title="Style swipes">
        {swipes.length ? (
          <>
            <Chips
              items={swipeCounts.map(([reaction, count]) => `${optionLabel(reaction)}: ${count}`)}
            />
            <More label={`Show all ${swipes.length} swipes`}>
              <Table
                label="Style swipes"
                columns={['Card', 'Reaction']}
                rows={swipes.map(([card, reaction]) => [
                  <span className="font-mono text-xs">{card}</span>,
                  optionLabel(String(reaction)),
                ])}
              />
            </More>
          </>
        ) : (
          <NotAnswered />
        )}
      </Sub>
      <Sub title="Style signals">
        {signals.length ? (
          <>
            <Chips
              items={reactionCounts(
                signals.map(([, signal]) =>
                  isDoc(signal) && typeof signal['verdict'] === 'string'
                    ? signal['verdict']
                    : 'no reaction',
                ),
              ).map(([reaction, count]) => `${optionLabel(reaction)}: ${count}`)}
            />
            <More label={`Show all ${signals.length} signals`}>
              <Table
                label="Style signals"
                columns={['Card', 'Details']}
                rows={signals.map(([card, signal]) => [
                  <span className="font-mono text-xs">{card}</span>,
                  <AnswerValue value={signal} />,
                ])}
              />
            </More>
          </>
        ) : (
          <NotAnswered />
        )}
      </Sub>
    </Topic>
  );
}

const WORK_KEYS = [
  'workStatus',
  'workStatusOther',
  'occupation',
  'workMode',
  'workDress',
  'workDays',
  'dayOffs',
  'weekdayActivities',
  'weekendActivities',
  'workOutfits',
  'outfitSets',
  'sleeves',
  'necklines',
];

/** Every key some topic shows; "Other answers" lists whatever else is stored. */
const SHOWN_KEYS = [
  ...ABOUT_KEYS,
  'expectation',
  'frustration',
  'size_profile',
  'fits_loved',
  'fits_avoided',
  'aesthetic_leanings',
  'colour_preferences',
  'occasion_mix',
  'style_swipes',
  'style_signals',
  'style_deck_completed_at',
  ...WORK_KEYS,
  'weeklyRoutine',
  'capture_choice',
  'styling_context',
];

function WorkTopic({ answers }: { answers: Answers }) {
  const rows = answers.rows(WORK_KEYS);
  const routine = answers.get('weeklyRoutine');
  const days = isDoc(routine) ? Object.entries(routine) : [];
  return (
    <Topic
      title="Work & week"
      summary={digest([
        text(answers.get('workStatus')),
        text(answers.get('occupation')),
        text(answers.get('workMode')),
      ])}
    >
      <DetailList items={rows} />
      <Sub title="Weekly routine">
        {days.length ? (
          <Table
            label="Weekly routine"
            columns={['Day', 'Plan']}
            rows={days.map(([day, plan]) => [optionLabel(day), <AnswerValue value={plan} />])}
          />
        ) : (
          <NotAnswered />
        )}
      </Sub>
    </Topic>
  );
}

const COLOUR_KEYS = ['season', 'undertone', 'depth', 'temperature', 'source'];

/** The member's real colour analysis, from the stored result rather than a quality flag. */
export function ColourAnalysisTopic({
  colour,
}: {
  colour: AdminMemberColorAnalysis | null | undefined;
}) {
  const doc: Doc = colour ?? {};
  const known = new Set([...COLOUR_KEYS, 'palette', 'palette_bins', 'summary'].map(norm));
  const extra = Object.entries(doc).filter(([key]) => !known.has(norm(key)));
  const palette = (colour?.palette ?? []).filter(Boolean);
  return (
    <Topic
      title="Colour analysis"
      summary={
        isEmpty(colour)
          ? 'Not analysed'
          : digest([text(colour?.season), text(colour?.undertone), text(colour?.depth)])
      }
    >
      {isEmpty(colour) ? (
        <NotAnswered>No colour analysis is stored for this member.</NotAnswered>
      ) : (
        <>
          <DetailList
            items={[
              ...COLOUR_KEYS.map((key): [ReactNode, ReactNode] => [
                answerLabel(key),
                <AnswerValue value={doc[key]} />,
              ]),
              ...extra.map(([key, value]): [ReactNode, ReactNode] => [
                answerLabel(key),
                <AnswerValue value={value} field={key} />,
              ]),
            ]}
          />
          <Sub title="Palette">
            {palette.length ? (
              <ul className="flex flex-wrap gap-3" aria-label="Palette">
                {palette.map((swatch, index) => (
                  <li key={`${swatch}-${index}`}>
                    {HEX.test(swatch.trim()) ? <Swatch hex={swatch.trim()} /> : optionLabel(swatch)}
                  </li>
                ))}
              </ul>
            ) : (
              <NotAnswered>No palette stored</NotAnswered>
            )}
            {colour?.palette_bins?.length ? (
              <DetailList
                items={[['Palette families', <AnswerValue value={colour.palette_bins} />]]}
              />
            ) : null}
          </Sub>
          <Sub title="Summary">
            {colour?.summary ? (
              <p className="text-sm text-secondary">{colour.summary}</p>
            ) : (
              <NotAnswered>No summary stored</NotAnswered>
            )}
          </Sub>
        </>
      )}
    </Topic>
  );
}

function yesNo(value: boolean | null | undefined) {
  return value == null ? <NotAnswered>Not recorded</NotAnswered> : value ? 'Yes' : 'No';
}

function BodygramDetails({ scan }: { scan: AdminMemberOnboardingScan }) {
  const bodygram = scan.bodygram;
  if (!bodygram) return <NotAnswered>No scan service details</NotAnswered>;
  const rows: Array<[ReactNode, ReactNode]> = Object.entries(bodygram).map(([key, value]) => {
    if (key === 'heightMm' && typeof value === 'number')
      return ['Height', `${(value / 10).toLocaleString()} cm`];
    if (key === 'weightG' && typeof value === 'number')
      return ['Weight', `${(value / 1000).toLocaleString()} kg`];
    return [answerLabel(key), <AnswerValue value={value} field={key} />];
  });
  return <DetailList items={rows} />;
}

function ScanTopic({
  data,
  onRefresh,
}: {
  data: AdminMemberOnboardingAnswers;
  onRefresh: () => void | Promise<void>;
}) {
  const { scan, images } = data;
  const count = scan.successful_scan_count ?? 0;
  return (
    <Topic
      title="Body scan"
      summary={digest([
        scan.scan_deferred ? 'Skipped for later' : undefined,
        count ? `${count} successful ${count === 1 ? 'scan' : 'scans'}` : 'No successful scans',
        scan.body_shape && `Body shape: ${optionLabel(scan.body_shape)}`,
      ])}
    >
      <DetailList
        items={[
          ['Skipped the scan for later', yesNo(scan.scan_deferred)],
          ['Successful scans', count.toLocaleString()],
          [
            'Body shape',
            scan.body_shape ? (
              optionLabel(scan.body_shape)
            ) : (
              <NotAnswered>Not recorded</NotAnswered>
            ),
          ],
          ['Last scanned', when(scan.last_scanned_at, 'Never')],
          ['Body profile last updated', when(scan.profile_updated_at)],
        ]}
      />
      <div className="grid grid-cols-2 gap-4 sm:max-w-md">
        <Photo
          url={images.scan_front_url}
          alt="Scan photo, front"
          absentDetail="No front scan photo is stored."
          onRefresh={onRefresh}
        />
        <Photo
          url={images.scan_side_url}
          alt="Scan photo, side"
          absentDetail="No side scan photo is stored."
          onRefresh={onRefresh}
        />
      </div>
      <Sub title="Measurements">
        <KeyValueTable label="Scan measurements" doc={scan.measurements} />
      </Sub>
      <More label="Scan service details and raw measurements">
        <div className="flex flex-col gap-4">
          <BodygramDetails scan={scan} />
          <KeyValueTable label="Raw scan measurements" doc={scan.raw_measurements} />
        </div>
      </More>
    </Topic>
  );
}

export type CurrentTwin = {
  url: string | null;
  signedAt: number;
  onRefresh: () => void | Promise<void>;
};

function PhotosTopic({
  answers,
  data,
  currentTwin,
  onRefresh,
}: {
  answers: Answers;
  data: AdminMemberOnboardingAnswers;
  currentTwin?: CurrentTwin | undefined;
  onRefresh: () => void | Promise<void>;
}) {
  const { images } = data;
  const captured = [images.portrait_url, images.twin_source_url].filter(Boolean).length;
  return (
    <Topic
      title="Photos"
      summary={
        captured ? `${captured} of 2 onboarding photos stored` : 'No onboarding photos stored'
      }
    >
      <DetailList items={answers.rows(['capture_choice'])} />
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <Photo
          url={images.portrait_url}
          alt="Portrait"
          absentDetail="No portrait is stored."
          onRefresh={onRefresh}
        />
        <Photo
          url={images.twin_source_url}
          alt="Twin source photo"
          absentDetail="No photo was stored for making the twin."
          onRefresh={onRefresh}
        />
        {currentTwin ? (
          // Signed by the onboarding read, so it refreshes through that read.
          <SignatureExpiryBoundary
            signedAt={currentTwin.signedAt}
            onRefresh={currentTwin.onRefresh}
          >
            <Photo
              url={currentTwin.url}
              alt="Current twin (for comparison)"
              absentDetail="The member has no current twin."
              onRefresh={currentTwin.onRefresh}
            />
          </SignatureExpiryBoundary>
        ) : null}
      </div>
    </Topic>
  );
}

const CONSENT_KINDS: Record<string, string> = {
  marketing: 'Marketing messages',
  training: 'Photos may train our styling models',
  brand_sharing: 'Share data with brands',
};

const LEGAL_DOCUMENTS: Record<AdminMemberLegalConsent['document'], string> = {
  terms: 'Terms of service',
  privacy: 'Privacy policy',
};

function consentKind(kind: string) {
  return CONSENT_KINDS[kind] ?? answerLabel(kind);
}

function ConsentsTopic({
  member,
  history,
  legal,
}: {
  member?: ConsumerDetail | undefined;
  history: AdminMemberConsentEvent[];
  legal: AdminMemberLegalConsent[];
}) {
  const timeline = [...history].sort((left, right) =>
    right.occurred_at.localeCompare(left.occurred_at),
  );
  const current = (value: boolean | undefined) =>
    member ? value ? 'Yes' : 'No' : <NotAnswered>Member record not loaded</NotAnswered>;
  return (
    <Topic
      title="Consents"
      summary={digest([
        legal.length
          ? legal.map((item) => `${LEGAL_DOCUMENTS[item.document]} v${item.version}`).join(', ')
          : 'Terms not accepted',
        `${history.length} consent ${history.length === 1 ? 'change' : 'changes'}`,
      ])}
    >
      <Sub title="Current consents">
        <DetailList
          items={[
            [CONSENT_KINDS['training'], current(member?.training_consent)],
            [CONSENT_KINDS['marketing'], current(member?.marketing_consent)],
            [CONSENT_KINDS['brand_sharing'], current(member?.brand_sharing_consent)],
          ]}
        />
      </Sub>
      <Sub title="Terms and privacy">
        {legal.length ? (
          <Table
            label="Legal acceptance"
            columns={['Document', 'Version', 'Accepted']}
            rows={legal.map((item) => [
              LEGAL_DOCUMENTS[item.document],
              item.version,
              when(item.accepted_at),
            ])}
          />
        ) : (
          <NotAnswered>No acceptance of the terms or privacy policy is recorded.</NotAnswered>
        )}
      </Sub>
      <Sub title="Consent history">
        {timeline.length ? (
          <ol className="flex flex-col divide-y divide-secondary rounded-lg border border-secondary">
            {timeline.map((event, index) => (
              <li
                key={`${event.kind}-${event.occurred_at}-${index}`}
                className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm"
              >
                <span className="font-medium text-secondary">
                  {consentKind(event.kind)}: {event.granted ? 'Gave consent' : 'Withdrew consent'}
                </span>
                <span className="text-xs text-tertiary">
                  {optionLabel(event.source)} · {when(event.occurred_at)}
                </span>
              </li>
            ))}
          </ol>
        ) : (
          <NotAnswered>No consent changes are recorded.</NotAnswered>
        )}
      </Sub>
    </Topic>
  );
}

function RemindersTopic({ reminders }: { reminders: AdminMemberOnboardingReminder[] }) {
  return (
    <Topic
      title="Reminders"
      summary={
        reminders.length
          ? `${reminders.length} unfinished-onboarding ${reminders.length === 1 ? 'reminder' : 'reminders'}`
          : 'No reminders scheduled'
      }
    >
      {reminders.length ? (
        <Table
          label="Onboarding reminders"
          columns={[
            'Status',
            'Scheduled for',
            'Delivery',
            'Delivered',
            'Attempts',
            'Error',
            'Finished',
          ]}
          rows={reminders.map((reminder) => [
            <Status value={reminder.status} />,
            when(reminder.scheduled_for),
            reminder.delivery_status ? (
              optionLabel(reminder.delivery_status)
            ) : (
              <NotAnswered>None</NotAnswered>
            ),
            when(reminder.delivered_at, 'Not delivered'),
            reminder.attempts.toLocaleString(),
            reminder.error_code ? (
              <span className="font-mono text-xs">{reminder.error_code}</span>
            ) : (
              <NotAnswered>None</NotAnswered>
            ),
            when(reminder.completed_at, 'Not finished'),
          ])}
        />
      ) : (
        <NotAnswered>
          No reminders to finish onboarding have been scheduled for this member.
        </NotAnswered>
      )}
    </Topic>
  );
}

function OtherTopic({ answers }: { answers: Answers }) {
  const left = answers.unclaimed();
  if (!left.length) return null;
  return (
    <Topic
      title="Other answers"
      summary={`${left.length} more stored ${left.length === 1 ? 'answer' : 'answers'}`}
    >
      <DetailList
        items={left.map(({ key, value }): [ReactNode, ReactNode] => [
          answerLabel(key),
          <AnswerValue value={value} field={key} />,
        ])}
      />
    </Topic>
  );
}

/* ---------------------------------------------------------------- panel */

export function OnboardingAnswersView({
  data,
  receivedAt,
  onRefresh,
  member,
  currentTwin,
}: {
  data: AdminMemberOnboardingAnswers;
  receivedAt: number;
  onRefresh: () => void | Promise<void>;
  member?: ConsumerDetail | undefined;
  currentTwin?: CurrentTwin | undefined;
}) {
  const preferences: Doc = isDoc(data.preferences) ? data.preferences : {};
  const context: Doc = isDoc(preferences['styling_context']) ? preferences['styling_context'] : {};
  const answers = new Answers(preferences, context);
  return (
    <SignatureExpiryBoundary
      signedAt={receivedAt}
      onRefresh={onRefresh}
      ttlMs={ANSWERS_SIGNED_URL_TTL_MS}
    >
      <div className="flex flex-col gap-3">
        <AboutTopic answers={answers} />
        <SizesTopic answers={answers} data={data} />
        <StyleTopic answers={answers} />
        <WorkTopic answers={answers} />
        <ColourAnalysisTopic colour={data.color_analysis} />
        <ScanTopic data={data} onRefresh={onRefresh} />
        <PhotosTopic
          answers={answers}
          data={data}
          currentTwin={currentTwin}
          onRefresh={onRefresh}
        />
        <ConsentsTopic
          member={member}
          history={data.consent_history ?? []}
          legal={data.legal_consents ?? []}
        />
        <RemindersTopic reminders={data.onboarding_reminders ?? []} />
        <OtherTopic answers={answers} />
      </div>
    </SignatureExpiryBoundary>
  );
}

export function OnboardingAnswersPanel({
  id,
  member,
  currentTwin,
}: {
  id: string;
  member?: ConsumerDetail | undefined;
  currentTwin?: CurrentTwin | undefined;
}) {
  // Not live: each poll would re-sign the photos and reload them. The read
  // runs again when the page reloads or when an expired photo fails.
  const source = useInsight<AdminMemberOnboardingAnswers>(
    () => memberOnboardingAnswersApiV1InternalUsersIdOnboardingAnswersGet({ path: { id } }),
    [id],
    { live: false },
  );
  // Keeps the answers on screen while the photos are re-signed; a failure
  // rejects, so the expiry boundary can say the photos could not be refreshed.
  const refresh = () => source.refresh({ preserve: true });
  return (
    <Section
      title="What the member told us"
      description="Every answer and photo stored from this member’s onboarding. Open a topic to see the detail."
    >
      <InsightState
        source={{ state: source.state, refresh: () => void source.refresh() }}
        subject="this member’s onboarding answers"
      >
        {(data, receivedAt) => (
          <OnboardingAnswersView
            data={data}
            receivedAt={receivedAt}
            onRefresh={refresh}
            member={member}
            currentTwin={currentTwin}
          />
        )}
      </InsightState>
    </Section>
  );
}
